import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, User as UserIcon, Home, CreditCard, Globe, MessagesSquare, Users, Target, ClipboardCheck, Bug } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Toaster } from "sonner";
import { tr } from "@/lib/i18n";
import LeoWidget from "@/components/LeoWidget";
import GuidedTour from "@/components/GuidedTour";
import ActivationChoice from "@/components/ActivationChoice";
import TheatricalSpotlight from "@/components/TheatricalSpotlight";
import MetaConsentBar from "@/components/MetaConsentBar";
import { useEffect } from "react";
import { loadPixel, mpTrack, getConsent } from "@/lib/metaPixel";

export default function Layout({ children }) {
  const { user, logout, access, lang, setLang } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = (k) => tr(k, lang);

  const toggleLang = () => setLang(lang === "pl" ? "en" : lang === "en" ? "ja" : "pl");

  // Meta Pixel PageView on every route change AFTER consent. If consent was
  // given in a previous browser session, `loadPixel()` boots the pixel on the
  // first render. If consent === "rejected" or null, both calls are no-ops.
  useEffect(() => {
    if (getConsent() === "accepted") {
      loadPixel();
      mpTrack("PageView");
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen relative">
      {/* Global theatrical spotlight — fixed, centered on the viewport,
          rendered beneath all content. Automatically hidden under .theme-light. */}
      <TheatricalSpotlight />
      <header className="relative z-10 border-b border-stone-900/80 bg-stone-950/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => navigate(user ? "/dashboard" : "/")}
            className="flex items-baseline gap-2"
          >
            <span className="serif text-2xl text-stone-100 tracking-wide">LEO</span>
            <span className="mono text-[10px] tracking-[0.35em] text-amber-500 uppercase">{t("brand.tagline")}</span>
          </button>
          <div className="flex items-center gap-1" data-onboarding="nav">
            <button
              data-testid="lang-toggle-btn"
              onClick={toggleLang}
              className="ghost-btn !py-2 !px-3 text-xs"
              title={t("nav.language")}
            >
              <Globe size={13} />
              <span className="mono tracking-widest">{lang === "ja" ? "日本語" : lang.toUpperCase()}</span>
            </button>
            {user && (
              <>
                <NavLink data-testid="nav-dashboard" to="/dashboard" className={({isActive}) => `ghost-btn !py-2 !px-3 text-sm ${isActive ? "!border-amber-500/60 !text-amber-400" : ""}`}>
                  <Home size={14}/> {t("nav.scenes")}
                </NavLink>
                <NavLink data-testid="nav-leo" to="/leo" className={({isActive}) => `ghost-btn !py-2 !px-3 text-sm ${isActive ? "!border-amber-500/60 !text-amber-400" : ""}`}>
                  <MessagesSquare size={14}/> Leo
                </NavLink>
                <NavLink data-testid="nav-crm" to="/crm" className={({isActive}) => `ghost-btn !py-2 !px-3 text-sm ${isActive ? "!border-amber-500/60 !text-amber-400" : ""}`}>
                  <Users size={14}/> CRM
                </NavLink>
                <NavLink data-testid="nav-career" to="/career" className={({isActive}) => `ghost-btn !py-2 !px-3 text-sm ${isActive ? "!border-amber-500/60 !text-amber-400" : ""}`}>
                  <Target size={14}/> {t("path.dash_tile_eyebrow")}
                </NavLink>
                <NavLink data-testid="nav-ops" to="/ops/tasks" className={({isActive}) => `ghost-btn !py-2 !px-3 text-sm ${isActive ? "!border-amber-500/60 !text-amber-400" : ""}`}>
                  <ClipboardCheck size={14}/> {t("tasks.dash_tile_eyebrow")}
                </NavLink>
                <NavLink data-testid="nav-pricing" to="/pricing" className={({isActive}) => `ghost-btn !py-2 !px-3 text-sm ${isActive ? "!border-amber-500/60 !text-amber-400" : ""}`}>
                  <CreditCard size={14}/> {t("nav.plans")}
                </NavLink>
                <NavLink data-testid="nav-profile" to="/profile" className={({isActive}) => `ghost-btn !py-2 !px-3 text-sm ${isActive ? "!border-amber-500/60 !text-amber-400" : ""}`}>
                  <UserIcon size={14}/> {user.name?.split(" ")[0] || (lang === "pl" ? "Ja" : "Me")}
                </NavLink>
                <button data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className="ghost-btn !py-2 !px-3 text-sm" title={t("nav.logout")}>
                  <LogOut size={14}/>
                </button>
              </>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="nav-bug-report-btn" className="ghost-btn !py-2 !px-3 text-sm" title={lang === "pl" ? "Zgłoś błąd" : "Report a bug"}>
                  <Bug size={14}/>
                </button>
              </PopoverTrigger>
              <PopoverContent data-testid="nav-bug-report-popup" align="end" className="w-64 bg-stone-950 border-stone-800 text-stone-200 text-sm">
                {lang === "pl" ? "Znalazłeś błąd? Poinformuj nas wysyłając maila na " : "Found a bug? Let us know by emailing "}
                <a data-testid="nav-bug-report-email" href="mailto:leo@zarzeczny.eu" className="text-amber-400 underline">leo@zarzeczny.eu</a>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {user && access && (
          <div className="max-w-6xl mx-auto px-6 pb-3 -mt-1 flex items-center justify-between text-xs text-stone-400">
            <span className="mono">
              {t("hdr.plan")}: <span className="text-stone-200 uppercase tracking-widest">{
                access.plan === "free" ? "7 DAY FREE" :
                access.plan === "single" ? "Single Scene" :
                access.plan === "sprint" ? "48h Sprint" :
                access.plan === "monthly" ? "LEO Monthly" :
                access.plan === "annual" ? "LEO Annual" :
                access.plan
              }</span>
              {access.plan === "free" && (
                <> · {t("hdr.free_scenes")}: <span className="text-amber-500">{Math.max(0, access.free_limit - access.scenes_used_free)}/{access.free_limit}</span></>
              )}
              {access.scene_credits > 0 && <> · {t("hdr.credits")}: <span className="text-amber-500">{access.scene_credits}</span></>}
              {access.plan_expires_at && <> · {t("hdr.expires")}: <span className="text-stone-300">{new Date(access.plan_expires_at).toLocaleDateString(lang === "pl" ? "pl-PL" : "en-US")}</span></>}
            </span>
          </div>
        )}
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10">{children}</main>
      <LeoWidget />
      <GuidedTour />
      <ActivationChoice />
      <MetaConsentBar />
      <Toaster theme="dark" position="bottom-left" richColors />
    </div>
  );
}
